import {
  FIELD_BIOME_GROUND_EDGE_GUARD,
  FIELD_BIOME_LAYERS,
  FIELD_BIOME_MAX_SPRITES,
  FIELD_BIOME_MIN_VISIBLE_ALPHA,
  FIELD_BIOME_SUBLAYERS,
} from '../configs/fieldBiomeEffects';
import {getFieldBiomeEffect} from '../configs/biomeEffects';
import {BIOME_EFFECT_BUILDERS} from '../utils/effects/biome';
import {createBiomeContext} from '../utils/effects/biome/biomeContext';
import {randomIn, resolveWindVector, smoothstep, writeHiddenPose} from '../utils/effects/biome/biomeMath';
import {resolveGroundEdgeFade} from '../utils/effects/buildCloudField';
import {ensureBiomeTextures} from '../utils/effects/fieldBiomeTextures';
import {resolveViewRect} from '../utils/effects/flightView';

// Группа, которой не хватило места (спрайтов или живых групп), пробует снова
// через столько миллисекунд
const RETRY_DELAY_MS = 600;
// Кадр игрока узнать не удалось: в такой кадр не попадает ничего
const NO_FRAME = Object.freeze({left: 0, right: 0, top: 0, bottom: 0});
/**
 * В кадре ли игрока точка. Кадр — тот же, что у голосов птиц (см.
 * utils/effects/flightView): null — камеры нет, и, как птицы, видимым считается
 * все; кадр без высоты (панель интерфейса закрыла холст целиком) не вмещает
 * ничего
 */
const checkIfInFrame = (frame, x, y) => (
  frame === null
  || (frame.bottom > frame.top && x >= frame.left && x <= frame.right && y >= frame.top && y <= frame.bottom)
);
/**
 * Видимость частицы наземного слоя у края картинки пейзажа: сам край гасит
 * затемнение картинки (оно лежит поверх этих слоев), здесь — только узкая
 * полоса, чтобы частица не вышла за картинку на черное поле
 */
const resolveGroundEdgeGuard = (rect, x, y) => {
  const side = Math.min(rect.right - rect.left, rect.bottom - rect.top);
  const distance = Math.min(x - rect.left, rect.right - x, y - rect.top, rect.bottom - y);
  return smoothstep(
    side * FIELD_BIOME_GROUND_EDGE_GUARD.fromShare, side * FIELD_BIOME_GROUND_EDGE_GUARD.toShare, distance
  );
};

/** Эффект подложки по перечням: построитель и его числа; null — эффекта нет */
const resolveRegisteredEffect = (groundBackgroundId) => {
  const assignment = getFieldBiomeEffect(groundBackgroundId);
  const builder = assignment ? BIOME_EFFECT_BUILDERS[assignment.kind] : null;
  return builder ? {builder, params: assignment.params} : null;
};

/**
 * ЭФФЕКТ ПОДЛОЖКИ: у каждой подложки поля — своя небольшая анимация, родная ее
 * пейзажу (порывы ветра над лугом, поземка, блики соли, огоньки над болотом…).
 * Какой подложке какой эффект и его числа — configs/biomeEffects, геометрия —
 * utils/effects/biome.
 *
 * КАК УСТРОЕНО. Эффект — это одна или несколько «дорожек». Дорожка по своему
 * расписанию рождает ГРУППЫ частиц (порыв ветра, вспышка бликов, один клуб
 * пара); у группы есть срок жизни, а поза каждой частицы — чистая функция
 * времени, прошедшего с рождения группы. Здесь нет ни одного числа эффекта:
 * контроллер только ведет расписание, держит спрайты и ставит их по позам.
 *
 * Дорожка (см. utils/effects/biome/*):
 *   {
 *     schedule: {firstDelayMs: {min, max}, intervalMs: {min, max}, maxGroups, prewarm}
 *               или {standing: true} — группа одна и живет, пока жив эффект;
 *     buildGroup({rng, view, liveGroups}) -> {durationMs, particles: [...]} | null;
 *     resolvePose(group, particle, elapsedMs, out) -> out;
 *     usesWind: false — дорожке ветер матча не нужен: при его смене она идет
 *               как шла (у построителя тот же признак — на все дорожки разом);
 *     tag — метка дорожки: по ней хозяин контроллера решает, можно ли дорожке
 *               рождать группы (canSpawn), и находит ее живые группы
 *               (findLiveGroups) — так вьюга ждет свой звук и звучит с ним в
 *               лад, а порыв ветра шумит, когда он в кадре игрока
 *   }
 * Частица: {layer, texture, tint, sublayer?} — слой (FIELD_BIOME_LAYERS),
 * текстура и краска; остальное — свои числа построителя. sublayer (0..3) —
 * порядок внутри слоя: частица с большим номером рисуется поверх (ореол блика —
 * 0, сам блик — 1). Поза: {x, y, alpha, width, height, rotation, tint?} в
 * координатах мира.
 *
 * СПРАЙТЫ НЕ СОЗДАЮТСЯ И НЕ УНИЧТОЖАЮТСЯ НА ХОДУ: отработавший спрайт прячется
 * и ждет следующей частицы своего слоя. Каждое добавление и удаление объекта
 * сцены пересортировывает весь ее список отрисовки, а в нем тысячи объектов.
 *
 * Время — время кадра, которое приходит снаружи (FieldAmbience.update): в
 * скрытой вкладке эффект стоит и после возврата продолжает с того же места.
 *
 * В КАДРЕ ЛИ ГРУППА. У дорожки с меткой может быть звук, а звук порыва слышен,
 * только когда порыв видит игрок (см. scenes/FieldEffectSounds). Поэтому о
 * каждой группе такой дорожки известно, насколько она в кадре (inViewAlpha):
 * наибольшая плотность среди ее частиц, чья середина сейчас в кадре игрока; 0 —
 * в кадре ее нет или она еще не проступила. Плотность — та, с которой частица
 * нарисована (с затуханием у края пейзажа), кадр — тот же, что у голосов птиц
 * (utils/effects/flightView: то, что показывает камера, без закрытого панелями
 * интерфейса и с отступом от краев). Считается это там же, где частицы ставятся
 * по местам, — второго прохода по ним нет; кадр берется раз за шаг и только
 * когда на поле есть группы дорожек с меткой: остальным эффектам он не стоит
 * ничего. Узнать кадр не удалось — эффект идет как шел, а его группы считаются
 * вне кадра.
 *
 * Эффект — украшение: любой его сбой гасится здесь же — эффект выключается до
 * конца сцены, а облака, птицы и само поле живут дальше.
 */
export default class FieldBiomeEffects {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [options]
   * @param {Function} [options.rng] — источник случайности (в тестах — заглушка)
   * @param {number} [options.linearFilter] — режим сглаживания текстур из кода
   * @param {Function} [options.resolveEffect] — какой эффект у подложки:
   *   (идентификатор) -> {builder, params} | null. По умолчанию — по перечням
   *   configs/biomeEffects и utils/effects/biome; в тестах эффекта — он сам
   * @param {Function} [options.canSpawn] — можно ли дорожке сейчас рождать
   *   группы: (дорожка) -> boolean. Спрашивается только у дорожек с меткой
   *   (tag); «нет» — очередь пропускается и вскоре спрашивается снова
   */
  constructor(scene, {
    rng = Math.random, linearFilter, resolveEffect = resolveRegisteredEffect, canSpawn = () => true,
  } = {}) {
    this.scene = scene;
    this.rng = rng;
    this.linearFilter = linearFilter;
    this.resolveEffect = resolveEffect;
    this.canSpawn = canSpawn;
    this.isFailed = false;
    this.context = null;
    this.effect = null;
    // Дорожки: {track, index, nextInMs, groups: [{group, elapsedMs, sprites, inViewAlpha}]}
    this.tracks = [];
    // Дорожки, снятые при смене ветра: их группы доживают свой срок. index —
    // место дорожки в эффекте: по нему новая дорожка знает свои прежние группы
    this.fadingTracks = [];
    // Спрайты: все созданные и свободные по слоям
    this.sprites = [];
    this.idleSprites = {};
    this.busySpriteCount = 0;
    // Поза частицы пишется в один и тот же объект: расчет идет каждый кадр
    this.pose = writeHiddenPose({});
    // Кадр игрока на этом шаге (см. resolveFrame): undefined — еще не узнавали
    this.frame = undefined;
    this.isFrameFailureReported = false;
  }

  /**
   * Пейзаж лег под поле (или поле изменилось) — эффект строится заново.
   *
   * @param {Object} params
   * @param {{left: number, right: number, top: number, bottom: number}} params.rect —
   *   прямоугольник картинки пейзажа
   * @param {number} params.hexSize
   * @param {Array<{x: number, y: number}>} [params.cellCenters] — центры игровых ячеек
   * @param {{sample: Function}} [params.coverage] — карта покрытия поля
   * @param {{direction: number, tiltRad: number}} [params.drift] — ветер матча
   * @param {string} params.groundBackgroundId
   * @param {Array<{x: number, y: number}>} [params.mountains] — центры ячеек с горами
   * @returns {boolean} построен ли эффект
   */
  setField(params = {}) {
    return this.guard('построение', () => {
      this.clear();
      const effect = this.resolveEffect(params.groundBackgroundId);
      const context = createBiomeContext(params);
      if (!effect || !context) {
        return false;
      }
      if (!ensureBiomeTextures(this.scene, effect.builder.textures, {linearFilter: this.linearFilter})) {
        return false;
      }

      this.context = context;
      this.effect = effect;
      this.startTracks();
      return true;
    }, false);
  }

  /**
   * Ветер матча изменился (партия началась на уже нарисованном поле): группы,
   * летевшие по прежнему ветру, доживают свой срок, новые родятся по новому —
   * в свой черед: расписание дорожки идет как шло. Дорожки, которым ветер не
   * нужен, не трогаются вовсе.
   *
   * @param {{direction: number, tiltRad: number}} drift
   */
  setDrift(drift) {
    this.guard('смена ветра', () => {
      if (!this.context || !drift) {
        return;
      }
      const isSameDrift = drift.direction === this.context.drift.direction && drift.tiltRad === this.context.drift.tiltRad;
      if (isSameDrift) {
        return;
      }
      this.context = {...this.context, drift, wind: resolveWindVector(drift)};
      // Эффекту без ветра (блики, круги на воде, стадо) перестраиваться незачем:
      // иначе на старте партии его группы на миг задвоились бы
      if (this.effect.builder.usesWind === false) {
        return;
      }
      const previous = this.tracks;
      previous.forEach((state) => {
        if (state.track.usesWind === false) {
          return;
        }
        if (state.track.schedule?.standing) {
          // Постоянные группы пересоздаются сразу, остальные — доживают
          this.releaseGroups(state);
        } else if (state.groups.length > 0) {
          this.fadingTracks.push(state);
        }
      });
      this.startTracks(previous);
    });
  }

  /**
   * Горы поля изменились (редактор поставил или убрал жетон горы): эффект не
   * перестраивается — новые группы родятся уже с нынешними горами.
   *
   * @param {Array<{x: number, y: number}>} mountains — центры ячеек с горами
   */
  setMountains(mountains) {
    this.guard('горы', () => {
      if (!this.context) {
        return;
      }
      // Список — тот же самый: построители держат его со дня создания дорожек
      this.context.mountains.length = 0;
      this.context.mountains.push(...(mountains || []));
    });
  }

  /**
   * Живые группы дорожек с такой меткой — и те, что доживают после смены ветра.
   *
   * @param {string} tag — метка дорожки
   * @returns {Array<{group: Object, elapsedMs: number, inViewAlpha: number}>}
   *   group — группа, как ее построила дорожка; elapsedMs — сколько она
   *   прожила; inViewAlpha — насколько она сейчас в кадре игрока (0..1):
   *   наибольшая плотность среди ее частиц, чья середина в кадре; 0 — в кадре
   *   ее нет или она еще не проступила (см. «В КАДРЕ ЛИ ГРУППА» выше)
   */
  findLiveGroups(tag) {
    const found = [];
    // Без метки искать нечего: дорожки без метки — не «дорожки с меткой undefined»
    if (this.isFailed || tag === undefined || tag === null) {
      return found;
    }
    [...this.fadingTracks, ...this.tracks].forEach((state) => {
      if (state.track.tag === tag) {
        state.groups.forEach(({group, elapsedMs, inViewAlpha}) => found.push({group, elapsedMs, inViewAlpha}));
      }
    });
    return found;
  }

  /**
   * Шаг эффекта: группы живут и уходят, расписание рождает новые.
   *
   * @param {number} stepMs — время кадра (уже ограниченное сверху)
   */
  update(stepMs) {
    this.guard('шаг', () => {
      if (!this.context || !(stepMs > 0)) {
        return;
      }
      const view = this.resolveView();
      // Камеру могли передвинуть: кадр игрока на этом шаге узнается заново
      this.frame = undefined;

      for (let index = this.fadingTracks.length - 1; index >= 0; index -= 1) {
        this.stepGroups(this.fadingTracks[index], stepMs);
        if (this.fadingTracks[index].groups.length === 0) {
          this.fadingTracks.splice(index, 1);
        }
      }
      this.tracks.forEach((state) => {
        this.stepGroups(state, stepMs);
        this.stepSchedule(state, stepMs, view);
      });
    });
  }

  /** Убрать эффект с поля; спрайты остаются в запасе до ухода сцены */
  clear() {
    this.guard('уборка', () => {
      // И группы, доживающие после смены ветра
      [...this.tracks, ...this.fadingTracks].forEach((state) => this.releaseGroups(state));
      this.tracks = [];
      this.fadingTracks = [];
      this.context = null;
      this.effect = null;
    });
  }

  /** Сцена уходит: спрайты убраны, новые не появятся */
  destroy() {
    this.context = null;
    this.effect = null;
    this.tracks = [];
    this.fadingTracks = [];
    const sprites = this.sprites;
    this.sprites = [];
    this.idleSprites = {};
    this.busySpriteCount = 0;
    sprites.forEach((sprite) => {
      try {
        sprite.destroy();
      } catch (error) {
        console.warn('[FieldBiomeEffects] Не удалось убрать спрайт эффекта подложки', error);
      }
    });
  }

  // ---------------------------------------------------------------- внутреннее

  /** Сбой эффекта не должен выйти наружу: эффект выключается до конца сцены */
  guard(title, action, fallback) {
    if (this.isFailed) {
      return fallback;
    }
    try {
      return action();
    } catch (error) {
      console.warn(`[FieldBiomeEffects] Эффект подложки отключен: сбой (${title})`, error);
      this.isFailed = true;
      try {
        this.destroy();
      } catch (destroyError) {
        console.warn('[FieldBiomeEffects] Не удалось убрать эффект подложки', destroyError);
      }
      return fallback;
    }
  }

  /**
   * Дорожки эффекта — заново, под текущий ветер.
   *
   * @param {Array} [previous] — прежние дорожки (при смене ветра). Те, что
   *   ветра не слушают, остаются как были; у остальных новая дорожка
   *   продолжает расписание прежней
   */
  startTracks(previous = []) {
    const {builder, params} = this.effect;
    const view = this.resolveView();
    // Дорожки строятся и между шагами времени (поле перестроили, ветер
    // сменился): кадр игрока с прошлого шага мог устареть
    this.frame = undefined;
    this.tracks = builder.createTracks(this.context, params).map((track, index) => {
      const old = previous[index];
      if (old && old.track.usesWind === false) {
        return old;
      }
      const state = {track, index, nextInMs: 0, groups: []};
      const {schedule} = track;
      if (schedule.standing) {
        this.spawnGroup(state, view, 0);
        return state;
      }
      // Смена ветра расписание не сбивает: следующая группа родится тогда же,
      // когда родилась бы у прежней дорожки
      state.nextInMs = old ? old.nextInMs : randomIn(this.rng, schedule.firstDelayMs);
      // Непрерывные эффекты не начинаются с пустого поля: первые группы
      // рождаются уже прожившими часть срока. После смены ветра поле не пусто:
      // на нем доживают прежние группы этой дорожки
      const prewarm = this.checkIfTrackAllowed(track)
        ? (schedule.prewarm || 0) - this.collectFadingGroups(index).length
        : 0;
      for (let count = 0; count < prewarm; count += 1) {
        this.spawnGroup(state, view, this.rng());
      }
      return state;
    });
  }

  /** Можно ли дорожке сейчас рождать группы: у дорожки с меткой решает хозяин контроллера */
  checkIfTrackAllowed(track) {
    return track.tag === undefined || Boolean(this.canSpawn(track));
  }

  /** Группы прежних дорожек того же места, которые еще доживают после смены ветра */
  collectFadingGroups(index) {
    const groups = [];
    this.fadingTracks.forEach((state) => {
      if (state.index === index) {
        groups.push(...state.groups);
      }
    });
    return groups;
  }

  stepSchedule(state, stepMs, view) {
    const {schedule} = state.track;
    if (schedule.standing) {
      return;
    }
    state.nextInMs -= stepMs;
    if (state.nextInMs > 0) {
      return;
    }
    // Доживающие группы прежнего ветра — тоже группы этой дорожки: вместе с
    // ними ее групп не больше положенного
    const liveCount = state.groups.length + this.collectFadingGroups(state.index).length;
    const isSpawned = liveCount < schedule.maxGroups && this.checkIfTrackAllowed(state.track)
      && this.spawnGroup(state, view, 0);
    state.nextInMs = isSpawned ? randomIn(this.rng, schedule.intervalMs) : RETRY_DELAY_MS;
  }

  /**
   * Родить группу дорожки.
   * @param {number} lifeShare — какую долю срока группа уже прожила (для
   *   групп, которыми эффект начинается)
   * @returns {boolean} родилась ли
   */
  spawnGroup(state, view, lifeShare) {
    // Живые группы дорожки — и те, что доживают после смены ветра: построитель
    // по ним решает, какое место уже занято
    const liveGroups = [...this.collectFadingGroups(state.index), ...state.groups].map((entry) => entry.group);
    const group = state.track.buildGroup({rng: this.rng, view, liveGroups});
    if (!group || !group.particles?.length) {
      return false;
    }
    if (this.busySpriteCount + group.particles.length > FIELD_BIOME_MAX_SPRITES) {
      return false;
    }

    const entry = {
      group, elapsedMs: Number.isFinite(group.durationMs) ? group.durationMs * lifeShare : 0, sprites: [], inViewAlpha: 0,
    };
    // Группа попадает в список сразу, спрайт за спрайтом: сорвись создание на
    // полпути, уборка найдет и уже взятые
    state.groups.push(entry);
    group.particles.forEach((particle) => entry.sprites.push(this.acquireSprite(particle)));
    this.placeGroup(state.track, entry);
    return true;
  }

  stepGroups(state, stepMs) {
    for (let index = state.groups.length - 1; index >= 0; index -= 1) {
      const entry = state.groups[index];
      entry.elapsedMs += stepMs;
      if (entry.elapsedMs >= entry.group.durationMs) {
        state.groups.splice(index, 1);
        entry.sprites.forEach((sprite) => this.releaseSprite(sprite));
      } else {
        this.placeGroup(state.track, entry);
      }
    }
  }

  placeGroup(track, entry) {
    const {group, elapsedMs, sprites} = entry;
    const {rect} = this.context;
    // Насколько группа в кадре игрока, нужно знать только у дорожки с меткой:
    // остальным кадр не узнается вовсе
    const isTracked = track.tag !== undefined;
    const frame = isTracked ? this.resolveFrame() : null;
    let inViewAlpha = 0;
    for (let index = 0; index < sprites.length; index += 1) {
      const sprite = sprites[index];
      const pose = track.resolvePose(group, group.particles[index], elapsedMs, this.pose);
      // У края картинки пейзажа все гаснет: дальше — черное поле. Наземные
      // слои гасит само затемнение края картинки, им хватает узкой полосы
      const resolveEdgeFade = sprite.biomeIsUnderEdgeFade ? resolveGroundEdgeGuard : resolveGroundEdgeFade;
      const alpha = pose.alpha > 0 ? pose.alpha * resolveEdgeFade(rect, pose.x, pose.y) : 0;
      if (!(alpha > FIELD_BIOME_MIN_VISIBLE_ALPHA) || !(pose.width > 0) || !(pose.height > 0)) {
        sprite.setVisible(false);
      } else {
        sprite.setPosition(pose.x, pose.y);
        sprite.setAlpha(Math.min(1, alpha));
        sprite.setDisplaySize(pose.width, pose.height);
        sprite.setRotation(pose.rotation);
        if (pose.tint !== undefined) {
          sprite.setTint(pose.tint);
        }
        sprite.setVisible(true);
        // В счет идет только нарисованная частица — с той плотностью, с какой она нарисована
        if (isTracked && alpha > inViewAlpha && checkIfInFrame(frame, pose.x, pose.y)) {
          inViewAlpha = alpha;
        }
      }
    }
    entry.inViewAlpha = Math.min(1, inViewAlpha);
  }

  /**
   * Кадр игрока на этом шаге — тот же, что у голосов птиц (то, что показывает
   * камера, без закрытого панелями интерфейса и с отступом от краев). Узнается
   * не чаще раза за шаг и только когда он кому-то нужен.
   *
   * @returns {{left: number, right: number, top: number, bottom: number}|null}
   *   null — камеры нет: видимым считается все
   */
  resolveFrame() {
    if (this.frame === undefined) {
      try {
        this.frame = resolveViewRect(this.scene, this.context.hexSize);
      } catch (error) {
        // Кадр нужен только звуку эффекта: его сбой самому эффекту не помеха.
        // Эффект идет как шел, а его группы считаются вне кадра — молчат
        if (!this.isFrameFailureReported) {
          this.isFrameFailureReported = true;
          console.warn('[FieldBiomeEffects] Не удалось узнать кадр игрока: эффект подложки идет без звука', error);
        }
        this.frame = NO_FRAME;
      }
    }
    return this.frame;
  }

  releaseGroups(state) {
    const groups = state.groups;
    state.groups = [];
    groups.forEach((entry) => entry.sprites.forEach((sprite) => this.releaseSprite(sprite)));
  }

  /** Спрайт под частицу: свободный со своего слоя или новый */
  acquireSprite({layer, texture, tint, sublayer = 0}) {
    const layerConfig = FIELD_BIOME_LAYERS[layer];
    if (!layerConfig) {
      throw new Error(`Неизвестный слой эффекта подложки: ${layer}`);
    }
    if (!Number.isInteger(sublayer) || sublayer < 0 || sublayer >= FIELD_BIOME_SUBLAYERS.count) {
      throw new Error(`Неверный порядок частицы в слое эффекта подложки: ${sublayer}`);
    }
    // Глубина у спрайта одна на всю жизнь: смена глубины пересортировывает
    // список отрисовки сцены. Поэтому запас спрайтов — свой у каждой глубины
    const poolKey = `${layer}:${sublayer}`;
    const idle = this.idleSprites[poolKey] || (this.idleSprites[poolKey] = []);
    let sprite = idle.pop();
    if (sprite) {
      sprite.setTexture(texture);
    } else {
      sprite = this.scene.add.image(0, 0, texture);
      this.sprites.push(sprite);
      sprite.biomePoolKey = poolKey;
      sprite.biomeIsUnderEdgeFade = Boolean(layerConfig.isUnderEdgeFade);
      sprite.setOrigin(0.5, 0.5);
      sprite.setDepth(layerConfig.depth + sublayer * FIELD_BIOME_SUBLAYERS.depthStep);
      if (layerConfig.isGlow) {
        sprite.setBlendMode('ADD');
      }
    }
    sprite.setTint(tint ?? 0xffffff);
    sprite.setVisible(false);
    this.busySpriteCount += 1;
    return sprite;
  }

  releaseSprite(sprite) {
    sprite.setVisible(false);
    this.idleSprites[sprite.biomePoolKey].push(sprite);
    this.busySpriteCount -= 1;
  }

  /**
   * Что сейчас видит камера (в координатах мира); null — камеры нет.
   * Размер кадра берется как размер окна камеры, деленный на масштаб
   */
  resolveView() {
    const camera = this.scene.cameras?.main;
    if (!camera || !(camera.zoom > 0) || !(camera.width > 0) || !(camera.height > 0)) {
      return null;
    }
    const halfWidth = camera.width / camera.zoom / 2;
    const halfHeight = camera.height / camera.zoom / 2;
    const centerX = camera.scrollX + camera.width / 2;
    const centerY = camera.scrollY + camera.height / 2;
    return {left: centerX - halfWidth, right: centerX + halfWidth, top: centerY - halfHeight, bottom: centerY + halfHeight};
  }
}
